// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Form, Button, message } from 'antd';
import {
  ArrowLeft,
  User,
  Wallet,
  CreditCard,
  Phone,
  Briefcase,
  Users,
  GraduationCap,
  Building2,
  Award,
  BookOpen,
  FolderOpen,
  Save,
} from 'lucide-react';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUpdatePersonalInfo, type PersonalInfoFormData } from '@/features/personnel/useUpdatePersonalInfo';
import { usersService } from '@/services/users.service';
import { UpdateProfileLayout } from '@/components/profile/UpdateProfileLayout';
import { UpdatePersonalInfoSection } from '@/components/profile/UpdatePersonalInfoSection';
import { UpdateHoroscopeSection } from '@/components/profile/UpdateHoroscopeSection';
import { UpdateIdDocumentsSection } from '@/components/profile/UpdateIdDocumentsSection';
import { UpdateContactInfoSection } from '@/components/profile/UpdateContactInfoSection';
import { UpdateWorkInfoSection } from '@/components/profile/UpdateWorkInfoSection';
import { UpdateFamilyDependentsSection } from '@/components/profile/UpdateFamilyDependentsSection';
import { UpdateEducationSection } from '@/components/profile/UpdateEducationSection';
import { UpdateBankInfoSection } from '@/components/profile/UpdateBankInfoSection';
import { UpdateCertificatesSkillsSection } from '@/components/profile/UpdateCertificatesSkillsSection';
import { UpdatePartyHistorySection } from '@/components/profile/UpdatePartyHistorySection';
import { UpdateFileStorageSection } from '@/components/profile/UpdateFileStorageSection';
import styles from './UpdatePersonalInfoPage.module.css';

export interface UpdatePersonalInfoPageProps {
  initialData?: PersonalInfoFormData;
  onSubmit?: (data: PersonalInfoFormData) => Promise<void>;
}

// Menu titles mapping – đồng bộ với UpdatePersonalProfilePage
const MENU_TITLES: Record<string, string> = {
  'personal-info': 'Thông tin cá nhân',
  horoscope: 'Lá số tử vi',
  'id-documents': 'Thông tin CC/CCCD/Hộ chiếu',
  'contact-info': 'Thông tin liên hệ',
  'work-info': 'Thông tin công việc',
  family: 'Gia đình và người phụ thuộc',
  education: 'Quá trình học tập',
  'bank-info': 'Thông tin ngân hàng',
  certificates: 'Chứng chỉ & Kỹ năng',
  'party-history': 'Lịch sử Đảng viên',
  'file-storage': 'Kho lưu trữ file cá nhân',
};

export const UpdatePersonalInfoPage: React.FC<UpdatePersonalInfoPageProps> = ({
  initialData: propInitialData,
  onSubmit,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as { personnel?: { id: string } } | null;
  const personnel = locationState?.personnel;
  const personnelId = personnel?.id ? parseInt(personnel.id, 10) : undefined;
  const isManagementUpdatePage = location.pathname === '/management/personnel/update-personal-info';

  const {
    form,
    loading,
    initialDataStatus,
    uploading,
    profileImage,
    setProfileImage,
    sidebarCollapsed,
    setSidebarCollapsed,
    selectedMenuKey,
    isHovered,
    setIsHovered,
    handleSubmit,
    handleValuesChange,
    handleCancel,
    handleImageUpload,
    handleMenuClick,
  } = useUpdatePersonalInfo(propInitialData, onSubmit);

  const [horoscopePreview, setHoroscopePreview] = useState<string | null>(null);
  const [horoscopeUploading, setHoroscopeUploading] = useState(false);
  const [horoscopeSelectedCount, setHoroscopeSelectedCount] = useState(0);

  const [citizenIdFrontPreview, setCitizenIdFrontPreview] = useState<string | null>(null);
  const [citizenIdBackPreview, setCitizenIdBackPreview] = useState<string | null>(null);
  const [passportPreview, setPassportPreview] = useState<string | null>(null);
  const [passportAdditionalPreview, setPassportAdditionalPreview] = useState<string | null>(null);
  const [idDocsUploading, setIdDocsUploading] = useState(false);
  const [certificateAttachmentUploaded, setCertificateAttachmentUploaded] = useState(false);
  const [isIdDocsDirty, setIsIdDocsDirty] = useState(false);
  const [citizenIdFrontFile, setCitizenIdFrontFile] = useState<File | null>(null);
  const [citizenIdBackFile, setCitizenIdBackFile] = useState<File | null>(null);
  const [passportCoverFile, setPassportCoverFile] = useState<File | null>(null);
  const [passportPhotoFile, setPassportPhotoFile] = useState<File | null>(null);

  // Fetch existing horoscope image (nếu có) để hiển thị preview ban đầu
  useEffect(() => {
    const fetchHoroscope = async () => {
      const userId = personnel?.id ? parseInt(personnel.id, 10) : NaN;
      if (!userId || isNaN(userId)) return;

      try {
        const horoscope = await usersService.getHoroscope(userId);
        if (horoscope?.imageUrl) {
          setHoroscopePreview(horoscope.imageUrl);
          setHoroscopeSelectedCount(1);
        }
      } catch {
        // Im lặng nếu không có lá số hoặc lỗi – không chặn luồng chính
      }
    };

    fetchHoroscope();
  }, [personnel?.id]);

  const handleHoroscopeBeforeUpload = useCallback(async (file: File): Promise<boolean> => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Vui lòng chọn file hình ảnh (JPG, PNG, WebP, ...)');
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      message.error('Kích thước ảnh tối đa 5MB');
      return false;
    }

    setHoroscopeUploading(true);

    try {
      const reader = new FileReader();
      reader.onload = () => {
        setHoroscopePreview(reader.result as string);
        setHoroscopeSelectedCount(1);
      };
      reader.readAsDataURL(file);

      // TODO: gọi API upload lá số tử vi khi backend sẵn sàng
    } finally {
      setHoroscopeUploading(false);
    }

    // Ngăn Ant Design tự upload
    return false;
  }, []);

  const validateImageFile = (file: File): boolean => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Vui lòng chọn file hình ảnh (JPG, PNG, WebP, ...)');
      return false;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      message.error('Kích thước ảnh tối đa 5MB');
      return false;
    }

    return true;
  };

  const previewImageFile = (file: File, onPreview: (previewUrl: string) => void) => {
    const reader = new FileReader();
    reader.onload = () => {
      onPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const createIdDocBeforeUploadHandler = useCallback(
    (
      setPreview: (value: string | null) => void,
      setFile: (value: File | null) => void,
      label: string,
    ) =>
      async (file: File): Promise<boolean> => {
        if (!validateImageFile(file)) {
          return false;
        }

        setIdDocsUploading(true);

        try {
          previewImageFile(file, (previewUrl) => {
            setPreview(previewUrl);
            setIsIdDocsDirty(true);
          });

          setFile(file);
          message.info(`${label} đã được chọn. Bấm "Cập nhật" để lưu thay đổi.`);
        } finally {
          setIdDocsUploading(false);
        }

        return false;
      },
    [],
  );

  useEffect(() => {
    const fetchIdCards = async () => {
      if (!personnelId || Number.isNaN(personnelId)) return;

      try {
        const cards = await usersService.getIdCards(personnelId);
        const approvedCards = cards.filter(
          (card) => (card.status || '').toLowerCase() === 'approved',
        );

        const idCardFront = approvedCards.find(
          (card) => card.documentType === 'id_card_front',
        )?.fileUrl;
        const idCardBack = approvedCards.find(
          (card) => card.documentType === 'id_card_back',
        )?.fileUrl;
        const passportCover = approvedCards.find(
          (card) => card.documentType === 'passport_cover',
        )?.fileUrl;
        const passportPhoto = approvedCards.find(
          (card) => card.documentType === 'passport_additional' || card.documentType === 'passport_photo',
        )?.fileUrl;

        if (idCardFront && !isIdDocsDirty && !citizenIdFrontFile) {
          setCitizenIdFrontPreview(idCardFront);
        }
        if (idCardBack && !isIdDocsDirty && !citizenIdBackFile) {
          setCitizenIdBackPreview(idCardBack);
        }
        if (passportCover && !isIdDocsDirty && !passportCoverFile) {
          setPassportPreview(passportCover);
        }
        if (passportPhoto && !isIdDocsDirty && !passportPhotoFile) {
          setPassportAdditionalPreview(passportPhoto);
        }
      } catch {
        // ignore
      }
    };

    fetchIdCards();
  }, [
    personnelId,
    isIdDocsDirty,
    citizenIdFrontFile,
    citizenIdBackFile,
    passportCoverFile,
    passportPhotoFile,
  ]);

  const handleSubmitWithIdDocs = useCallback(async () => {
    const pendingUploads: Array<{
      file: File | null;
      documentType: string;
      side: 'front' | 'back';
    }> = [
      { file: citizenIdFrontFile, documentType: 'id_card_front', side: 'front' },
      { file: citizenIdBackFile, documentType: 'id_card_back', side: 'back' },
      { file: passportCoverFile, documentType: 'passport_cover', side: 'front' },
      { file: passportPhotoFile, documentType: 'passport_additional', side: 'back' },
    ];

    const hasIdDocs = pendingUploads.some((item) => item.file);
    const result = await handleSubmit();
    if (result === 'failed') return;
    if (result === 'no-changes' && !hasIdDocs && !certificateAttachmentUploaded) {
      message.warning('Không có thông tin nào được thay đổi');
      return;
    }

    const finish = () => {
      if (isManagementUpdatePage) {
        if (personnel?.id) {
          navigate(`/management/personnel/detail/${personnel.id}`, {
            state: { refresh: true, timestamp: Date.now() },
          });
        } else {
          window.location.href = '/management/personnel';
        }
      } else {
        window.location.href = '/personal/profile';
      }
    };

    if (!hasIdDocs) {
      finish();
      return;
    }

    const targetUserId = personnelId;
    if (!targetUserId || Number.isNaN(targetUserId)) {
      message.error('Không tìm thấy nhân sự để cập nhật giấy tờ tùy thân');
      return;
    }

    setIdDocsUploading(true);
    try {
      for (const item of pendingUploads) {
        if (!item.file) continue;

        const uploadResponse = await usersService.uploadIdCard(
          item.file,
          item.documentType,
          item.side,
          isManagementUpdatePage ? targetUserId : undefined,
        );

        if (!uploadResponse?.success) {
          message.error(uploadResponse?.message || 'Không thể upload ảnh giấy tờ. Vui lòng thử lại.');
          return;
        }
      }

      setIsIdDocsDirty(false);
      setCitizenIdFrontFile(null);
      setCitizenIdBackFile(null);
      setPassportCoverFile(null);
      setPassportPhotoFile(null);

      message.success(
        isManagementUpdatePage
          ? 'Đã cập nhật giấy tờ tùy thân thành công.'
          : 'Đã gửi yêu cầu cập nhật giấy tờ, chờ phê duyệt.',
      );

      finish();
    } catch (error: any) {
      message.error(error?.message || 'Không thể upload ảnh giấy tờ. Vui lòng thử lại.');
    } finally {
      setIdDocsUploading(false);
    }
  }, [
    handleSubmit,
    citizenIdFrontFile,
    citizenIdBackFile,
    passportCoverFile,
    passportPhotoFile,
    personnelId,
    isManagementUpdatePage,
    personnel?.id,
    navigate,
    certificateAttachmentUploaded,
  ]);

  const menuItems: MenuProps['items'] = [
    {
      key: 'personal-info',
      icon: <User size={22} />,
      label: 'Thông tin cá nhân',
    },
    {
      key: 'horoscope',
      icon: <Wallet size={22} />,
      label: 'Lá số',
    },
    {
      key: 'id-documents',
      icon: <CreditCard size={22} />,
      label: 'Giấy tờ tùy thân',
    },
    {
      key: 'contact-info',
      icon: <Phone size={22} />,
      label: 'Thông tin liên hệ',
    },
    {
      key: 'work-info',
      icon: <Briefcase size={22} />,
      label: 'Thông tin công việc',
    },
    {
      key: 'family',
      icon: <Users size={22} />,
      label: 'Gia đình và người phụ thuộc',
    },
    {
      key: 'education',
      icon: <GraduationCap size={22} />,
      label: 'Quá trình học tập',
    },
    {
      key: 'bank-info',
      icon: <Building2 size={22} />,
      label: 'Thông tin ngân hàng',
    },
    {
      key: 'certificates',
      icon: <Award size={22} />,
      label: 'Chứng chỉ & Kỹ năng',
    },
    {
      key: 'party-history',
      icon: <BookOpen size={22} />,
      label: 'Lịch sử Đảng viên',
    },
    {
      key: 'file-storage',
      icon: <FolderOpen size={22} />,
      label: 'Kho lưu trữ file cá nhân',
    },
  ];

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.header}>
          <div className={styles.headerLeft} onClick={handleCancel}>
            <ArrowLeft size={20} className={styles.backIcon} />
            <h2 className={styles.pageTitle}>Cập nhật thông tin</h2>
          </div>
          <div className={styles.headerActions}>
            <Button size="large" onClick={handleCancel} className={styles.cancelButton}>
              Hủy
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleSubmitWithIdDocs}
              loading={loading || idDocsUploading}
              disabled={initialDataStatus !== 'ready'}
              className={styles.submitButton}
              icon={<Save size={16} />}
            >
              Cập nhật
            </Button>
          </div>
        </div>
        <UpdateProfileLayout
          sidebarCollapsed={sidebarCollapsed}
          isHovered={isHovered}
          setIsHovered={setIsHovered}
          setSidebarCollapsed={setSidebarCollapsed}
          selectedMenuKey={selectedMenuKey}
          menuItems={menuItems}
          onMenuClick={handleMenuClick}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmitWithIdDocs} onValuesChange={handleValuesChange} className={styles.form}>
            {initialDataStatus !== 'ready' && (
              <Alert
                type={initialDataStatus === 'error' ? 'error' : 'info'}
                showIcon
                message={initialDataStatus === 'error'
                  ? 'Không tải được đầy đủ dữ liệu hồ sơ. Hãy tải lại trang trước khi cập nhật.'
                  : 'Đang tải dữ liệu hồ sơ, học vấn và người thân...'}
                style={{ marginBottom: 16 }}
              />
            )}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                {MENU_TITLES[selectedMenuKey] || 'Thông tin cá nhân'}
              </h3>

              {selectedMenuKey === 'personal-info' && (
                <UpdatePersonalInfoSection
                  uploading={uploading}
                  profileImage={profileImage}
                  setProfileImage={setProfileImage}
                  handleImageUpload={handleImageUpload}
                />
              )}

              {selectedMenuKey !== 'personal-info' && (
                <>
                  {selectedMenuKey === 'horoscope' && (
                    <UpdateHoroscopeSection
                      horoscopePreview={horoscopePreview}
                      horoscopeUploading={horoscopeUploading}
                      horoscopeSelectedCount={horoscopeSelectedCount}
                      onClear={() => {
                        setHoroscopePreview(null);
                        setHoroscopeSelectedCount(0);
                      }}
                      onBeforeUpload={handleHoroscopeBeforeUpload}
                    />
                  )}
                  {selectedMenuKey === 'id-documents' && (
                    <UpdateIdDocumentsSection
                      userId={personnelId}
                      citizenIdFrontPreview={citizenIdFrontPreview}
                      citizenIdBackPreview={citizenIdBackPreview}
                      passportPreview={passportPreview}
                      passportAdditionalPreview={passportAdditionalPreview}
                      isUploading={idDocsUploading}
                      onBeforeUploadCitizenFront={createIdDocBeforeUploadHandler(
                        setCitizenIdFrontPreview,
                        setCitizenIdFrontFile,
                        'Ảnh CCCD mặt trước',
                      )}
                      onBeforeUploadCitizenBack={createIdDocBeforeUploadHandler(
                        setCitizenIdBackPreview,
                        setCitizenIdBackFile,
                        'Ảnh CCCD mặt sau',
                      )}
                      onBeforeUploadPassportCover={createIdDocBeforeUploadHandler(
                        setPassportPreview,
                        setPassportCoverFile,
                        'Ảnh hộ chiếu',
                      )}
                      onBeforeUploadPassportPhoto={createIdDocBeforeUploadHandler(
                        setPassportAdditionalPreview,
                        setPassportPhotoFile,
                        'Ảnh khác hộ chiếu',
                      )}
                      onClearCitizenFront={() => {
                        setCitizenIdFrontPreview(null);
                        setCitizenIdFrontFile(null);
                        setIsIdDocsDirty(true);
                      }}
                      onClearCitizenBack={() => {
                        setCitizenIdBackPreview(null);
                        setCitizenIdBackFile(null);
                        setIsIdDocsDirty(true);
                      }}
                      onClearPassportCover={() => {
                        setPassportPreview(null);
                        setPassportCoverFile(null);
                        setIsIdDocsDirty(true);
                      }}
                      onClearPassportPhoto={() => {
                        setPassportAdditionalPreview(null);
                        setPassportPhotoFile(null);
                        setIsIdDocsDirty(true);
                      }}
                    />
                  )}
                  {selectedMenuKey === 'contact-info' && <UpdateContactInfoSection />}
                  {selectedMenuKey === 'work-info' && (
                    <UpdateWorkInfoSection userId={personnelId} form={form} />
                  )}
                  {selectedMenuKey === 'family' && (
                    <UpdateFamilyDependentsSection userId={personnelId} form={form} />
                  )}
                  {selectedMenuKey === 'education' && (
                    <UpdateEducationSection userId={personnelId} form={form} />
                  )}
                  {selectedMenuKey === 'bank-info' && (
                    <UpdateBankInfoSection userId={personnelId} form={form} />
                  )}
                  {selectedMenuKey === 'certificates' && (
                    <UpdateCertificatesSkillsSection userId={personnelId} form={form} onAttachmentUploaded={() => setCertificateAttachmentUploaded(true)} />
                  )}
                  {selectedMenuKey === 'party-history' && (
                    <UpdatePartyHistorySection userId={personnelId} form={form} />
                  )}
                  {selectedMenuKey === 'file-storage' && (
                    <UpdateFileStorageSection userId={personnelId} />
                  )}
                </>
              )}
            </div>
          </Form>
        </UpdateProfileLayout>
      </div>
    </div>
  );
};
